/**
 * Material and color swap.
 * Applies fabric color/texture to all fabric-flagged meshes, and leg color
 * to all leg-flagged meshes, in an assembly.
 */

import * as THREE from "three";
import type { AssemblyResult } from "./Assembly";
import type { FabricSwatch, LegOption } from "@/types";

const textureLoader = new THREE.TextureLoader();
const textureCache = new Map<string, THREE.Texture>();

export function applyFabric(assembly: AssemblyResult, fabric: FabricSwatch): void {
  const color = new THREE.Color(fabric.color ?? "#cdc6b6");
  let texture: THREE.Texture | null = null;
  if (fabric.textureUrl) {
    texture = getTexture(fabric.textureUrl);
  }
  assembly.group.traverse((o) => {
    const mesh = o as THREE.Mesh;
    const mat = mesh.material as THREE.MeshStandardMaterial | undefined;
    if (!mat) return;
    if (mat.userData.isLeg) return;
    if (!mat.userData.isFabric && !mesh.userData.isFabric) return;
    mat.color.copy(color);
    if (texture) {
      // Runtime weave swap (a real download): apply the loaded texture.
      mat.map = texture;
    } else if (mat.userData.bakedMap !== undefined) {
      // No runtime weave for this fabric → RESTORE the GLB's baked weave map, so
      // a swap back to a baked fabric shows its real weave (not the last runtime
      // one). mat.color multiplies it — the zero-byte in-shader tint path.
      mat.map = mat.userData.bakedMap as THREE.Texture | null;
    }
    // else: material never had a baked map (e.g. procedural) — keep whatever's there.
    mat.needsUpdate = true;
  });
}

export function applyLeg(assembly: AssemblyResult, leg: LegOption): void {
  const color = new THREE.Color(leg.color);
  assembly.group.traverse((o) => {
    const mesh = o as THREE.Mesh;
    const mat = mesh.material as THREE.MeshStandardMaterial | undefined;
    if (!mat) return;
    if (!mat.userData.isLeg && !mesh.userData.isLeg) return;
    mat.color.copy(color);
    // tweak metalness/roughness based on visual character of the leg
    const id = leg.id.toLowerCase();
    if (id.includes("brass") || id.includes("chrome") || id.includes("steel")) {
      mat.metalness = 0.85;
      mat.roughness = 0.25;
    } else if (id.includes("oak") || id.includes("wood")) {
      mat.metalness = 0;
      mat.roughness = 0.7;
    } else {
      mat.metalness = 0.2;
      mat.roughness = 0.5;
    }
    mat.needsUpdate = true;
  });
}

/** Recolor any mesh/material whose name matches `target` (case-insensitive
 *  substring) to `color`. Drives generic config-declared swatch rows (e.g. a lamp's
 *  "Shade color" → target "Shade"/"Fabric", "Base finish" → target "Base"/"Metal").
 *  Name-based so a product just names the part it wants recolourable in its config. */
export function recolorByName(assembly: AssemblyResult, target: string, color: string): void {
  const c = new THREE.Color(color);
  const t = target.trim().toLowerCase();
  if (!t) return;
  assembly.group.traverse((o) => {
    const mesh = o as THREE.Mesh;
    if (!mesh.isMesh) return;
    const meshHit = (mesh.name || "").toLowerCase().includes(t);
    const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
    for (const m of mats) {
      const sm = m as THREE.MeshStandardMaterial | undefined;
      if (!sm || !sm.color) continue;
      // Never recolor an emissive "-on" toggle variant (e.g. "Fabric-on",
      // "Glass-on"): the substring "fabric" would otherwise also catch it.
      if (/-on$/i.test(sm.name || "")) continue;
      if (meshHit || (sm.name || "").toLowerCase().includes(t)) {
        sm.color.copy(c);
        sm.needsUpdate = true;
      }
    }
  });
}

/** GPU max anisotropy, set once at engine init. GLTFLoader leaves baked maps at
 *  anisotropy=1, which blurs/shimmers grazing-angle surfaces (wood grain, fabric
 *  weave, shade curve). */
let MAX_ANISOTROPY = 4;
export function setMaxAnisotropy(n: number): void {
  MAX_ANISOTROPY = Math.max(1, Math.floor(n) || 1);
}

const ANISO_SLOTS = ["map", "normalMap", "roughnessMap", "metalnessMap", "aoMap", "emissiveMap"] as const;

/** Raise every baked texture on an assembly to the GPU max anisotropy. Call after
 *  the assembly is built (GLB maps default to 1×). Idempotent. */
export function maximizeAnisotropy(assembly: AssemblyResult): void {
  assembly.group.traverse((o) => {
    const mesh = o as THREE.Mesh;
    if (!mesh.isMesh) return;
    const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
    for (const m of mats) {
      const sm = m as THREE.MeshStandardMaterial | undefined;
      if (!sm) continue;
      for (const slot of ANISO_SLOTS) {
        const tex = sm[slot] as THREE.Texture | null | undefined;
        if (tex && tex.anisotropy !== MAX_ANISOTROPY) {
          tex.anisotropy = MAX_ANISOTROPY;
          tex.needsUpdate = true;
        }
      }
    }
  });
}

function getTexture(url: string): THREE.Texture {
  const cached = textureCache.get(url);
  if (cached) return cached;
  const tex = textureLoader.load(url);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.wrapS = THREE.RepeatWrapping;
  tex.wrapT = THREE.RepeatWrapping;
  tex.anisotropy = MAX_ANISOTROPY;
  textureCache.set(url, tex);
  return tex;
}

/**
 * Predictive prefetch: warm the fabric texture cache for a weave swap so a later
 * material change is a cache hit (0 network). Populates the SAME `textureCache`
 * applyFabric reads (so it reuses the texture), AND resolves when the image
 * finishes — or fails — loading, so the caller can prefetch gently, one at a
 * time. Never rejects; a real applyFabric later behaves normally either way.
 */
export function prefetchFabricTexture(url: string): Promise<void> {
  if (textureCache.has(url)) return Promise.resolve();
  return new Promise<void>((resolve) => {
    const tex = textureLoader.load(url, () => resolve(), undefined, () => resolve());
    tex.colorSpace = THREE.SRGBColorSpace;
    tex.wrapS = THREE.RepeatWrapping;
    tex.wrapT = THREE.RepeatWrapping;
    tex.anisotropy = MAX_ANISOTROPY;
    textureCache.set(url, tex);
  });
}

export function clearTextureCache(): void {
  for (const t of textureCache.values()) t.dispose();
  textureCache.clear();
}
